import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import {
  OnboardingNavigationState,
  readOnboardingNavigationState,
} from '@/pages/onboarding/onboarding-navigation-state';
import {
  AnimatePresence,
  motion,
  Reorder,
  useReducedMotion,
} from 'motion/react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {} from '@/components/ui/popover';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  Pipette,
  Plus,
  Sparkles,
} from 'lucide-react';
import { cx } from 'class-variance-authority';
import { usePatchTenantCreationRequest } from '@/hooks/use-patch-tenant-request';
import {
  OnboardingPreviewProvider,
  useOnboardingPreview,
  useSetOnboardingPreview,
} from '@/pages/onboarding/onboarding-preview-context';
import {
  ZoomedIPhoneMockup,
  ZOOM_TRANSITION_DURATION_MS,
} from '@/pages/onboarding/mockup/zoomed-iphone-mockup';
import { AppOpenScene } from '@/pages/onboarding/mockup/scenes/app-open-scene';
import {
  OnboardingPageLayout,
  OnboardingPageHeader,
  OnboardingPageContent,
  OnboardingPageFooter,
} from '@/pages/onboarding/onboarding-page-layout';
import { DraggableTabRow } from '@/pages/onboarding/4-tab-bar-config.tab-row';
import { OnboardingStepsIndicator } from '@/pages/onboarding/onboarding-steps-indicator';
import { pickIconForNewTab } from '@/lib/onboarding-icon-catalog';
import {
  INTERNAL_TAB_PATHS,
  maskTabLink,
  MAX_TABS,
  PRESET_TABS,
  TabBarConfigFormValues,
  TabConfigFormValues,
  DEFAULT_ACCENT_COLOR,
} from '@/pages/onboarding/4-tab-bar-config.schema';
import { TAB_BAR_FORM_OPTIONS } from '@/pages/onboarding/4-tab-bar-config.form-options';
import { useMediaQuery } from '@/hooks/use-mobile';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import { TAB_BAR_FOCUS } from '@/pages/onboarding/mobile-studio-geometry';

// Pivots the zoom on the tab bar sitting at the very bottom of the screen
// (AppOpenScene/MockupTabBar, ~92% down the frame), instead of
// ZoomedIPhoneMockup's default icon-focused pivot.
const TAB_BAR_ZOOM_ORIGIN_Y = 92;
const TAB_BAR_ZOOM_ORIGIN = `50% ${TAB_BAR_ZOOM_ORIGIN_Y}%`;

const TAB_BAR_ZOOM_SCALE = 1.8;

// `zoomOrigin` only keeps its own point (the tab bar) pinned at its original
// screen position as the frame scales up — on its own that leaves the tab
// bar still sitting near the bottom edge, just bigger, not centered. Shifts
// the whole frame up by exactly enough to bring that pinned point from
// TAB_BAR_ZOOM_ORIGIN_Y down to the column's vertical center (50%); doesn't
// depend on TAB_BAR_ZOOM_SCALE (see ZoomedIPhoneMockup's zoomTranslateY).
const TAB_BAR_ZOOM_TRANSLATE_Y = `${50 - TAB_BAR_ZOOM_ORIGIN_Y}%`;

// AppLogoPage zooms itself back out to rest *before* navigating here (see
// its onContinue) — the actual "icon grows into the open app" effect is the
// browser's View Transition API morphing AppLogoPage's (now resting, normal
// size) icon into this page's AppOpenScene root, since both share the
// `onboarding-app-icon` viewTransitionName (see HomeScreenScene / AppOpenScene).
// That morph only fires on the real navigation that mounts this page, so
// this page must render AppOpenScene at rest (zoomed=false) from its very
// first paint — any pre-navigation zoom state has to live on the *previous*
// page, not be replayed here after mount via a separate scene swap.
//
// Same rationale as AppNameAndLinkPage's ZOOM_IN_DELAY_MS — lets the
// app-opening morph settle before our own push-in on the tab bar plays.
const ZOOM_IN_DELAY_MS = 500;

const ACCENT_COLOR_PRESETS = [
  '#1860FB',
  '#FF6B00',
  '#EF4444',
  '#F59E0B',
  '#22C55E',
  '#8B5CF6',
  '#EC4899',
  '#111827',
];

export default function TabBarConfigPage() {
  return (
    <OnboardingPreviewProvider>
      <TabBarConfigPageContent />
    </OnboardingPreviewProvider>
  );
}

function TabBarConfigPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const patchCreationRequest = usePatchTenantCreationRequest();
  const preview = useOnboardingPreview();
  const setPreview = useSetOnboardingPreview();
  const shouldReduceMotion = useReducedMotion();

  // NotificationPreviewPage's "Voltar" passes this so this page can tell
  // "arriving from AppLogoPage's icon morph" (the default/forward case)
  // apart from "arriving back from NotificationPreviewPage's own slide"
  // (see both `isZoomed` and `iconMorphEnabled` below, which each need to
  // behave differently depending on which one it is).
  const cameFromNotificationBack = Boolean(
    readOnboardingNavigationState(location.state).cameFromNotificationBack
  );

  // Starts unzoomed — AppOpenScene at rest, right as the "icon grows into
  // the open app" view transition from AppLogoPage lands — then flips to
  // zoomed shortly after (see ZOOM_IN_DELAY_MS) to push in on the tab bar.
  // "Voltar" flips it back to false to play the same push-in in reverse
  // before navigating away, so the outgoing frame is at rest too (matching
  // AppLogoPage's own rest state on arrival, for the morph back into the
  // icon — see its `arrivingFromTabBarBack` handling).
  //
  // Arriving back from NotificationPreviewPage is different: that page's
  // own "Voltar" already plays its slide *back* to this exact zoomed/
  // translated resting position before it navigates away (see its
  // TAB_BAR_RESTING_TRANSLATE_Y), so this page must mount already at that
  // same position too — starting unzoomed here would cut back to small,
  // then re-play the zoom-in entrance for no reason, which is the same
  // "ends in one place, next page starts in another" jump this flow's other
  // transitions were built to avoid.
  //
  // Below lg the page starts framed on the tab bar right away: AppLogoPage's
  // icon grows from the Studio's close-up into the app framed like this.
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);
  const [isZoomed, setIsZoomed] = useState(
    () => cameFromNotificationBack || !isDesktop
  );
  const [isLeaving, setIsLeaving] = useState(false);

  // AppOpenScene's `enableIconMorph` must only be on while the *other* side
  // of the current navigation is AppLogoPage's icon (see its own doc
  // comment) — NotificationPreviewPage's AppOpenScene never carries the tag,
  // so pairing this page's copy with it would make the browser treat the
  // mismatched tag as the element appearing/disappearing and run its own
  // ~0.45s position animation (see globals.css's
  // `::view-transition-group(onboarding-app-icon)`) racing our own
  // hand-timed one, which read as a teleport-then-catch-up. Starts disabled
  // when we've just arrived from NotificationPreviewPage's "Voltar" (same
  // mismatch, reversed), then flips back on shortly after so a *later*
  // "Voltar" to AppLogoPage still gets its own real icon-morph pairing.
  const [iconMorphEnabled, setIconMorphEnabled] = useState(
    () => !cameFromNotificationBack
  );

  useEffect(() => {
    if (iconMorphEnabled) {
      return;
    }
    const timeout = setTimeout(() => setIconMorphEnabled(true), 500);
    return () => clearTimeout(timeout);
  }, [iconMorphEnabled]);

  // Which tab's icon-picker popover is open, keyed by the tab's own `key`
  // (stable across reorders/removals) — lifted up here, instead of local
  // state inside each TabRow, so tapping a tab in the phone mockup can open
  // that specific row's picker from outside it (see `onPhoneTabClick`).
  const [openIconPickerKey, setOpenIconPickerKey] = useState<string | null>(
    null
  );
  const rowElementsRef = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    // No guard needed for the `cameFromNotificationBack` case where
    // `isZoomed` already started `true`: setting it to `true` again here is
    // a no-op React bails out of (same value), so it neither re-renders nor
    // replays ZoomedIPhoneMockup's entrance animation.
    if (shouldReduceMotion) {
      setIsZoomed(true);
      return;
    }
    const timeout = setTimeout(() => setIsZoomed(true), ZOOM_IN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [shouldReduceMotion]);

  const onBack = () => {
    if (isLeaving) {
      return;
    }
    setIsLeaving(true);
    // Below lg the app shrinks back into the icon from the tab bar framing.
    if (isDesktop) setIsZoomed(false);
    const delay =
      shouldReduceMotion || !isDesktop ? 0 : ZOOM_TRANSITION_DURATION_MS;
    setTimeout(() => {
      navigate('../app-logo', {
        viewTransition: true,
        state: { fromTabBarBack: true } satisfies OnboardingNavigationState,
      });
    }, delay);
  };

  const form = useForm<TabBarConfigFormValues>({
    ...TAB_BAR_FORM_OPTIONS,
    defaultValues: {
      // A previously-saved request has custom tab paths with the scheme
      // already added back on by the schema's transform (see
      // `normalizeTabLink`) — stripped again here so the field keeps
      // displaying the same scheme-less link the merchant typed.
      tabs: (preview.tabs && preview.tabs.length > 0
        ? preview.tabs
        : PRESET_TABS
      ).map((tab) =>
        INTERNAL_TAB_PATHS.has(tab.path)
          ? tab
          : { ...tab, path: maskTabLink(tab.path) }
      ),
      accentColor: preview.accentColor ?? DEFAULT_ACCENT_COLOR,
    },
  });

  const {
    register,
    handleSubmit,
    formState,
    watch,
    getValues,
    setValue,
    control,
  } = form;

  // Plain array field, managed by hand (append/remove as full-array
  // `setValue` calls) instead of `useFieldArray` — its `remove` was found to
  // leave a stale, keyless entry behind in the form's actual values (even
  // though its own `fields` output looked correctly shrunk), which then
  // resurfaced as a bad-keyed extra row the next time a tab was appended.
  // Each tab already carries its own stable `key` from creation, so it
  // doubles as the React list key below — nothing RHF's array id tracking
  // was adding for us here.
  const tabsValue = watch('tabs') ?? [];

  const accentColorValue = watch('accentColor');

  // `setPreview` comes from context and isn't memoized by the provider, so
  // it gets a new identity on every render — kept in a ref instead of a
  // dependency so the subscription below doesn't need to be recreated
  // (and, in turn, doesn't retrigger on every preview update it causes).
  const setPreviewRef = useRef(setPreview);
  useEffect(() => {
    setPreviewRef.current = setPreview;
  });

  // Subscribes once via RHF's callback form of `watch` (its documented
  // pattern for syncing external state) instead of a `useEffect` keyed off
  // `watch('tabs')`'s return value: that value is a fresh array on every
  // render, so an effect depending on it re-subscribes on every render too —
  // including the re-render triggered by this same effect's own `setPreview`
  // call — which free-runs the debounce forever. A single mount-time
  // subscription has no such feedback loop.
  useEffect(() => {
    // `watch(callback)` only fires on subsequent changes, not for the form's
    // initial values, so the mockup would otherwise start out with no tabs
    // at all until the merchant's first edit — prime it once up front.
    const initialValues = getValues();
    setPreviewRef.current({
      tabs: initialValues.tabs,
      accentColor: initialValues.accentColor || null,
    });

    let timeout: ReturnType<typeof setTimeout> | undefined;

    const subscription = watch(() => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        // Reads fresh via `getValues()` at commit time rather than using the
        // `value` this callback was invoked with, and drops anything
        // keyless defensively — belt and suspenders around the same kind of
        // transient inconsistency `removeTab`/`addCustomTab`/`addPreset`
        // above are already written to avoid at the source.
        const latest = getValues();
        const validTabs = (latest.tabs ?? []).filter(
          (tab): tab is TabConfigFormValues =>
            typeof tab?.key === 'string' && tab.key.length > 0
        );
        setPreviewRef.current({
          tabs: validTabs,
          accentColor: latest.accentColor || null,
        });
      }, 300);
    });

    return () => {
      subscription.unsubscribe();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [watch, getValues]);

  const canAddMoreTabs = tabsValue.length < MAX_TABS;

  const addPreset = (preset: TabBarConfigFormValues['tabs'][number]) => {
    if (!canAddMoreTabs) {
      return;
    }
    const key = `${preset.key}-${tabsValue.length}`;
    setValue('tabs', [...tabsValue, { ...preset, key }], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const addCustomTab = () => {
    if (!canAddMoreTabs) {
      return;
    }
    const randomIcon = pickIconForNewTab(
      new Set(tabsValue.map((tab) => tab.iconKey))
    );

    const newTab = {
      key: crypto.randomUUID(),
      title: '',
      path: '',
      iconKey: randomIcon.key,
    };
    setValue('tabs', [...tabsValue, newTab], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // Removes by the tab's own stable `key`, not its array index — after a
  // drag reorder, a row's rendered position and its index can momentarily
  // disagree (the drag settles via Reorder's own live-reordering, which
  // commits mid-drag, ahead of this component's next render), so an
  // index-captured-at-render-time closure can point at the wrong row by the
  // time the merchant actually clicks delete. The key is immune to that.
  const removeTab = (key: string) => {
    setValue(
      'tabs',
      tabsValue.filter((tab) => tab.key !== key),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  // Drag-to-reorder handler — shared by both drag surfaces (the form list's
  // `Reorder.Group` below, and the phone mockup's own tab bar, wired up via
  // AppOpenScene's `onTabReorder`). `Reorder` already diffs the dragged
  // value's new index against the others by their live layout position, so
  // the reordered array it hands back is the full, already-correctly-
  // ordered `tabs` array, just like `removeTab`'s filter.
  const reorderTabs = (newOrder: TabConfigFormValues[]) => {
    // Revalidated because RHF keys errors by array index: without it, an error
    // set on a row would stay behind at its old position instead of following
    // the row it belongs to.
    setValue('tabs', newOrder, { shouldDirty: true, shouldValidate: true });
    // Mirrors the reorder into the live preview immediately, instead of
    // waiting on the regular debounced form->preview sync (see the `watch`
    // subscription above) — that lag is fine for typing, but here it'd show
    // up as the dragged tab visibly snapping back to its old spot the
    // instant it's dropped, then re-settling into place up to 300ms later.
    setPreview({ tabs: newOrder });
  };

  // Tapping a tab in the phone mockup jumps straight to editing it: makes
  // it the active tab (same highlight as focusing its row — see
  // `activeTabKey`), opens its icon picker, and scrolls its row into view
  // in case the list is taller than the visible column.
  const onPhoneTabClick = (index: number) => {
    const tab = preview.tabs?.[index];
    if (!tab) {
      return;
    }
    setPreview({ activeTabKey: tab.key });
    setOpenIconPickerKey(tab.key);
    rowElementsRef.current
      .get(tab.key)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onSubmit = handleSubmit(
    async (values) => {
      await patchCreationRequest.mutateAsync({
        tabs: values.tabs,
        accentColor: values.accentColor,
      });
      // Flushed synchronously, not just `setIconMorphEnabled(false)` — the
      // view transition's snapshot of this (outgoing) page is captured the
      // instant `navigate()` runs, before React would otherwise have had a
      // chance to commit the state update. Without this, the tag is still
      // present in that snapshot and the mismatch/race described above
      // still happens.
      flushSync(() => setIconMorphEnabled(false));
      navigate('../notification', { viewTransition: true });
    },
    // A blocked submit used to leave the merchant with no feedback beyond
    // whichever per-row error happened to already be visible — easy to miss
    // if the invalid tab is scrolled out of view. Jump straight to the
    // first one so the (now also clearer, see `tabsBannerError` below)
    // error message always lands next to the field it's about.
    (formErrors) => {
      const firstInvalidIndex = Array.isArray(formErrors.tabs)
        ? formErrors.tabs.findIndex((tabError) => !!tabError)
        : -1;
      const firstInvalidTab =
        firstInvalidIndex >= 0 ? tabsValue[firstInvalidIndex] : undefined;
      if (firstInvalidTab) {
        rowElementsRef.current
          .get(firstInvalidTab.key)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  );

  const tabsLengthError =
    typeof formState.errors.tabs?.message === 'string'
      ? formState.errors.tabs.message
      : undefined;

  const hasInvalidTabItems =
    Array.isArray(formState.errors.tabs) &&
    formState.errors.tabs.some((tabError) => !!tabError);

  const tabsBannerError =
    tabsLengthError ??
    (hasInvalidTabItems
      ? 'Revise as abas destacadas em vermelho antes de continuar.'
      : undefined);

  const hasCustomAccentColor = !ACCENT_COLOR_PRESETS.some(
    (color) => color.toLowerCase() === accentColorValue?.toLowerCase()
  );

  const entrance = shouldReduceMotion
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

  return (
    <OnboardingPageLayout
      mobileFocus={isZoomed ? TAB_BAR_FOCUS : undefined}
      mockup={
        <ZoomedIPhoneMockup
          sceneKey="app-open"
          zoomed={isZoomed}
          zoomOrigin={TAB_BAR_ZOOM_ORIGIN}
          zoomScale={TAB_BAR_ZOOM_SCALE}
          zoomTranslateY={TAB_BAR_ZOOM_TRANSLATE_Y}
        >
          <AppOpenScene
            tabs={preview.tabs ?? []}
            accentColor={preview.accentColor ?? DEFAULT_ACCENT_COLOR}
            activeIndex={Math.max(
              0,
              preview.tabs?.findIndex(
                (tab) => tab.key === preview.activeTabKey
              ) ?? 0
            )}
            onTabClick={onPhoneTabClick}
            onTabReorder={reorderTabs}
            // Below lg the app opens inside AppLogoPage's phone instead.
            enableIconMorph={iconMorphEnabled && isDesktop}
          />
        </ZoomedIPhoneMockup>
      }
    >
      <OnboardingStepsIndicator currentStep="tab-bar" />

      <motion.div {...entrance} transition={{ duration: 0.35 }}>
        <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground max-lg:hidden">
          <Sparkles className="h-3 w-3" />
          Navegação do app
        </span>
        <OnboardingPageHeader
          title="Monte a barra de navegação"
          subtitle={
            <>
              Até 5 atalhos.
              <span className="hidden lg:inline">
                {' '}
                O resultado aparece ao vivo no celular ao lado.
              </span>
            </>
          }
          className="mb-4"
        />
      </motion.div>

      <OnboardingPageContent>
        <TooltipProvider delayDuration={150}>
          <form id="tab-bar-config-form" onSubmit={onSubmit}>
            <motion.div
              {...entrance}
              transition={{ duration: 0.35, delay: 0.06 }}
            >
              <Card className="rounded-2xl border-border/60 shadow-sm transition-shadow focus-within:shadow-md focus-within:shadow-primary/5">
                <CardContent className="space-y-4 pt-5">
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Abas do menu
                      </span>
                      <div
                        className="flex items-center gap-1.5"
                        aria-label={`${tabsValue.length} de ${MAX_TABS} abas usadas`}
                      >
                        {Array.from({ length: MAX_TABS }).map((_, i) => (
                          <span
                            key={i}
                            className={cx(
                              'h-1.5 w-5 rounded-full transition-colors',
                              i < tabsValue.length ? 'bg-primary' : 'bg-muted'
                            )}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {PRESET_TABS.map((preset) => {
                        const alreadyAdded = tabsValue.some(
                          (tab) => tab.path === preset.path
                        );
                        const disabled = alreadyAdded || !canAddMoreTabs;

                        return (
                          <motion.button
                            key={preset.key}
                            type="button"
                            whileTap={disabled ? undefined : { scale: 0.96 }}
                            disabled={disabled}
                            onClick={() => addPreset(preset)}
                            className={cx(
                              'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                              alreadyAdded
                                ? 'border-primary/30 bg-primary/5 text-primary'
                                : 'border-border bg-background hover:border-primary/40 hover:bg-primary/5',
                              !canAddMoreTabs &&
                                !alreadyAdded &&
                                'cursor-not-allowed opacity-50'
                            )}
                          >
                            {preset.title}
                            <AnimatePresence initial={false}>
                              {alreadyAdded && (
                                <motion.span
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{
                                    type: 'spring',
                                    stiffness: 500,
                                    damping: 20,
                                  }}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </motion.button>
                        );
                      })}
                    </div>

                    <Reorder.Group
                      as="div"
                      axis="y"
                      values={tabsValue}
                      onReorder={reorderTabs}
                      className="space-y-1.5"
                    >
                      {tabsValue.map((tab, index) => (
                        <DraggableTabRow
                          key={tab.key}
                          tab={tab}
                          control={control}
                          index={index}
                          setValue={setValue}
                          onRemove={() => removeTab(tab.key)}
                          onActivate={() =>
                            setPreview({ activeTabKey: tab.key })
                          }
                          rowRef={(el) => {
                            if (el) {
                              rowElementsRef.current.set(tab.key, el);
                            } else {
                              rowElementsRef.current.delete(tab.key);
                            }
                          }}
                          iconPickerOpen={openIconPickerKey === tab.key}
                          onIconPickerOpenChange={(open) =>
                            setOpenIconPickerKey(open ? tab.key : null)
                          }
                        />
                      ))}
                    </Reorder.Group>

                    {tabsBannerError && (
                      <div className="mt-2 flex items-start gap-1.5 text-sm text-red-500">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{tabsBannerError}</span>
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canAddMoreTabs}
                      onClick={addCustomTab}
                      className="mt-2 w-full border-dashed"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar aba personalizada
                    </Button>
                  </div>

                  <Separator />

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Cor de destaque
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {accentColorValue?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {ACCENT_COLOR_PRESETS.map((color) => {
                        const isSelected =
                          accentColorValue?.toLowerCase() ===
                          color.toLowerCase();

                        return (
                          <motion.button
                            key={color}
                            type="button"
                            aria-label={`Selecionar cor ${color}`}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.92 }}
                            className={cx(
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                              isSelected
                                ? 'border-foreground'
                                : 'border-transparent'
                            )}
                            style={{ backgroundColor: color }}
                            onClick={() =>
                              setValue('accentColor', color, {
                                shouldValidate: true,
                              })
                            }
                          >
                            <AnimatePresence>
                              {isSelected && (
                                <motion.span
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{
                                    type: 'spring',
                                    stiffness: 500,
                                    damping: 20,
                                  }}
                                >
                                  <Check className="h-3.5 w-3.5 text-white drop-shadow" />
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </motion.button>
                        );
                      })}

                      <label
                        aria-label="Escolher outra cor"
                        className={cx(
                          'relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-dashed text-muted-foreground transition-colors hover:border-foreground',
                          hasCustomAccentColor &&
                            'border-solid border-foreground text-foreground'
                        )}
                        style={
                          hasCustomAccentColor
                            ? { backgroundColor: accentColorValue }
                            : undefined
                        }
                      >
                        <Pipette className="h-3.5 w-3.5 mix-blend-difference invert" />
                        <input
                          type="color"
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                          {...register('accentColor')}
                        />
                      </label>
                    </div>
                    {formState.errors.accentColor?.message && (
                      <div className="mt-2 text-sm text-red-500">
                        {formState.errors.accentColor.message}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </form>
        </TooltipProvider>
      </OnboardingPageContent>

      <OnboardingPageFooter className="justify-between">
        <Button
          type="button"
          variant="outline"
          disabled={patchCreationRequest.isPending || isLeaving}
          onClick={onBack}
        >
          Voltar
        </Button>
        <Button
          type="submit"
          form="tab-bar-config-form"
          disabled={patchCreationRequest.isPending || isLeaving}
          className="min-w-[100px]"
        >
          {patchCreationRequest.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Continuar'
          )}
        </Button>
      </OnboardingPageFooter>
    </OnboardingPageLayout>
  );
}
